import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useT } from '@/i18n/I18nProvider'
import type { TranslationKey } from '@/i18n/dictionary'
import { Button, Select } from '@/components/ui'

/**
 * Where you are in a paginated list, and how to move.
 *
 * Written once because the same three mistakes kept appearing in each screen's
 * own copy:
 *
 * - **Controls only at the foot of the rows.** With fifty per page they sat
 *   four screens down, and a page you have to hunt to the bottom of to discover
 *   is a page that reads as having no pagination at all. Callers render this
 *   above the list as well as below.
 *
 * - **`1 / 2` instead of a range.** That only helps somebody who already knows
 *   to look for a second page. `1–25 od 63` is the thing that says there is
 *   more.
 *
 * - **Hidden on a single page.** Its absence is indistinguishable from a broken
 *   filter, so it renders either way — the arrows disappear, the count does not.
 */
export function DataPager({
  meta,
  perPage,
  onPerPage,
  onPage,
  /**
   * The whole "nothing here" sentence, as a key.
   *
   * It used to be a bare noun — `noun="korisnika"` — which this component glued
   * into `Nema {noun}`. That works in exactly one language: *korisnika* is a
   * genitive plural chosen to follow *nema*, and no translator can supply the
   * equivalent without knowing the frame it lands in. English would have
   * rendered "Nema users".
   *
   * A key for the finished sentence costs each caller one dictionary entry and
   * makes the fragment impossible to construct.
   */
  emptyKey = 'analytics.pager.empty',
}: {
  meta: {
    current_page: number
    last_page: number
    from: number | null
    to: number | null
    total: number
  }
  perPage: number
  onPerPage: (value: number) => void
  onPage: (value: number) => void
  emptyKey?: TranslationKey
}) {
  const t = useT()

  const multiPage = meta.last_page > 1

  return (
    <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
      {/*
        tabular-nums so the numbers do not jitter as pages change. On the
        paragraph rather than on a span around each number: the line is one
        template per language now, so there is nothing left to wrap.
      */}
      <p className="muted text-sm tabular-nums">
        {meta.total === 0
          ? t(emptyKey)
          : t('analytics.pager.range', {
              from: meta.from ?? '',
              to: meta.to ?? '',
              total: meta.total,
            })}
      </p>

      <div className="flex items-center gap-2">
        <Select
          value={String(perPage)}
          onChange={(event) => onPerPage(Number(event.target.value))}
          className="w-auto"
          aria-label={t('analytics.pager.perPage.label')}
        >
          {[25, 50, 100].map((size) => (
            <option key={size} value={size}>
              {t('analytics.pager.perPage', { count: size })}
            </option>
          ))}
        </Select>

        {multiPage && (
          <>
            <Button
              variant="secondary"
              disabled={meta.current_page <= 1}
              onClick={() => onPage(meta.current_page - 1)}
              aria-label={t('analytics.pager.prev')}
              className="px-3"
            >
              <ChevronLeft className="size-4" aria-hidden />
            </Button>

            <span className="muted min-w-16 text-center text-sm tabular-nums">
              {meta.current_page} / {meta.last_page}
            </span>

            <Button
              variant="secondary"
              disabled={meta.current_page >= meta.last_page}
              onClick={() => onPage(meta.current_page + 1)}
              aria-label={t('analytics.pager.next')}
              className="px-3"
            >
              <ChevronRight className="size-4" aria-hidden />
            </Button>
          </>
        )}
      </div>
    </div>
  )
}
